import Cookies from 'js-cookie';

import { getAdsMarketingConsent } from '@/shared/lib/adsConsent';

const CLICK_STORAGE_KEY = 'rask:microsoft-ads:click';
const CLICK_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const UUID_PATTERN = /^(?:[a-f0-9]{32}|[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/i;

type MicrosoftClick = { campaign_msclkid?: string; captured_at: string; msclkid: string };
// Preserve click age when storage fails, while respecting removal of a saved record.
let pageClick: MicrosoftClick | undefined;
let pendingStorageWrite = false;

export type MicrosoftAdsContext = {
  anonymous_id?: string;
  captured_at?: string;
  client_user_agent?: string;
  event_source_url?: string;
  marketing_consent: boolean;
  msclkid?: string;
  observed_at: string;
};

const normalizeUuid = (value?: null | string) => {
  if (!value || !UUID_PATTERN.test(value)) return undefined;
  return value.toLowerCase();
};

const getMicrosoftClick = (): MicrosoftClick | undefined => {
  let stored = pageClick;
  try {
    const candidate = JSON.parse(localStorage.getItem(CLICK_STORAGE_KEY) || 'null') as MicrosoftClick | null;
    if (candidate && normalizeUuid(candidate.msclkid) && Number.isFinite(Date.parse(candidate.captured_at))) {
      stored = candidate;
      pendingStorageWrite = false;
    } else if (!pendingStorageWrite) {
      stored = undefined;
    }
  } catch {
    /* Browser storage is optional. */
  }

  const incoming = normalizeUuid(new URLSearchParams(window.location.search).get('msclkid'));
  const alreadyObserved =
    incoming?.replaceAll('-', '') === window.__RASK_MICROSOFT_ADS_URL_CLICK__?.replaceAll('-', '');
  if (incoming) window.__RASK_MICROSOFT_ADS_URL_CLICK__ = incoming;
  const campaignEcho = incoming?.replaceAll('-', '') === stored?.campaign_msclkid?.replaceAll('-', '');
  // Generic campaign storage has no capture time, so do not import it into CAPI.
  // Keep the original observation time for the same click, including after expiry.
  // Focus/activity reads cannot turn an unchanged old tab into a new click.
  // A decorated marketing link can also echo a previously observed cookie ID.
  if (
    incoming &&
    (!stored || (!alreadyObserved && !campaignEcho)) &&
    incoming.replaceAll('-', '') !== stored?.msclkid.replaceAll('-', '')
  ) {
    stored = { campaign_msclkid: stored?.campaign_msclkid, captured_at: new Date().toISOString(), msclkid: incoming };
    pendingStorageWrite = true;
  }
  if (stored && pendingStorageWrite) {
    try {
      localStorage.setItem(CLICK_STORAGE_KEY, JSON.stringify(stored));
      pendingStorageWrite = false;
    } catch {
      pendingStorageWrite = true;
    }
  }
  pageClick = stored;
  const age = stored ? Date.now() - Date.parse(stored.captured_at) : NaN;
  return stored && age >= 0 && age <= CLICK_MAX_AGE_MS
    ? { captured_at: stored.captured_at, msclkid: stored.msclkid }
    : undefined;
};

export const getMicrosoftAdsContext = (): MicrosoftAdsContext | undefined => {
  const observed_at = new Date().toISOString();
  const consent = getAdsMarketingConsent();
  if (consent === undefined) return;
  if (!consent) return { marketing_consent: false, observed_at };

  const anonymous_id = normalizeUuid(Cookies.get('_uetvid')?.split('|')[0]);
  const click = getMicrosoftClick();
  return {
    marketing_consent: true,
    observed_at,
    ...click,
    ...(anonymous_id && { anonymous_id }),
    client_user_agent: navigator.userAgent.slice(0, 1024),
    event_source_url: `${window.location.origin}${window.location.pathname}`.slice(0, 2048),
  };
};
